import { create } from 'zustand';
import useEditorStore from '../../../shared/stores/useEditorStore';
import usePreparingScanStore from './usePreparingScanStore';
import * as THREE from 'three';
import { computeBoundsTree } from 'three-mesh-bvh';
import { createMeshFromSliceData, sliceShadedMeshAsync } from '../../../shared/lib/sliceMeshAsync';
import { runSlicePreviewDetach } from '../../../shared/lib/slicePreviewDetach';

const useSliceStore = create((set, get) => ({
  // --- STATE ---
  slicingPlane: null,       // The active THREE.Plane for slicing
  selectionDirection: 'upper', // 'upper' or 'lower'
  planeTransformMode: 'translate', // 'translate' | 'rotate' for the slice plane gizmo
  geometryHistory: [],      // An array of BufferGeometries for the undo stack

  // --- ACTIONS ---

  /** Starts the slice preview process. */
  startSlicePreview: (plane) => {
    set({ slicingPlane: plane, selectionDirection: 'upper' });
  },

  /** Updates the slicing plane without resetting the selection direction. */
  updateSlicingPlane: (plane) => {
    set({ slicingPlane: plane });
  },

  /** Set transform mode for the slice plane gizmo: 'translate' or 'rotate'. */
  setPlaneTransformMode: (mode) => {
    set({ planeTransformMode: mode === 'rotate' ? 'rotate' : 'translate' });
  },

  /** Toggles the selection between the upper and lower mesh parts. */
  invertSelection: () => {
    set((state) => ({
      selectionDirection: state.selectionDirection === 'upper' ? 'lower' : 'upper',
    }));
  },

  /**
   * Run mesh slice in worker, then push trim onto undo history.
   * Preview uses GPU clipping only; this is the first real geometry cut.
   */
  executeTrimAsync: async () => {
    const { slicingPlane, selectionDirection } = get();
    const { sceneRefs, setIsLoading, execCommand } = useEditorStore.getState();
    const originalMesh = sceneRefs?.shadedMesh;

    if (!slicingPlane || !originalMesh) return;

    setIsLoading(true, 'Trimming mesh...');
    try {
      const result = await sliceShadedMeshAsync(originalMesh, slicingPlane);
      if (!result?.upper || !result?.lower) return;

      const tempMat = new THREE.MeshStandardMaterial();
      const upperMesh = createMeshFromSliceData(
        result.upper,
        `${originalMesh.name}_upper`,
        originalMesh,
        tempMat,
      );
      const lowerMesh = createMeshFromSliceData(
        result.lower,
        `${originalMesh.name}_lower`,
        originalMesh,
        tempMat,
      );
      if (!upperMesh || !lowerMesh) return;

      const command = get().createTrimCommand({ upper: upperMesh, lower: lowerMesh });
      if (command) {
        runSlicePreviewDetach();
        execCommand(command);
      }
    } catch (error) {
      console.error('Trim failed:', error);
    } finally {
      setIsLoading(false);
    }
  },

  /** Build undo command from worker slice pieces (upper / lower meshes). */
  createTrimCommand: (activeSlice) => {
    const { selectionDirection } = get();
    const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
    const { clearSelection } = usePreparingScanStore.getState();
    const originalMesh = sceneRefs?.shadedMesh;

    if (!activeSlice?.upper || !activeSlice?.lower || !originalMesh) return null;

    const originalGeometry = originalMesh.geometry;
    const originalGeometryClone = new THREE.BufferGeometry().copy(originalGeometry);
    const originalMaterial = originalMesh.material;
    const originalMatrix = originalMesh.matrix.clone();
    const discardKey = selectionDirection === 'upper' ? 'lower' : 'upper';

    return {
      execute: () => {
        const meshToKeep = activeSlice[selectionDirection];
        const meshToDiscard = activeSlice[discardKey];
        const newMesh = new THREE.Mesh(meshToKeep.geometry, originalMaterial);

        newMesh.matrix.copy(originalMatrix);
        newMesh.matrixAutoUpdate = originalMesh.matrixAutoUpdate;

        if (newMesh.matrixAutoUpdate) {
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
        }

        if (!newMesh.geometry.attributes.normal) {
          newMesh.geometry.computeVertexNormals();
        }
        newMesh.geometry.computeBoundingBox();
        newMesh.geometry.computeBoundingSphere();
        computeBoundsTree.call(newMesh.geometry);

        meshToDiscard.geometry?.dispose();
        meshToKeep.material?.dispose?.();
        meshToDiscard.material?.dispose?.();

        get().clearSlicePreview();
        clearSelection();
        replaceShadedMesh(newMesh);
      },
      undo: () => {
        const restoredMesh = new THREE.Mesh(originalGeometryClone, originalMaterial);

        restoredMesh.matrix.copy(originalMatrix);
        restoredMesh.matrixAutoUpdate = originalMesh.matrixAutoUpdate;

        if (restoredMesh.matrixAutoUpdate) {
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
        }

        if (originalGeometryClone.boundsTree) {
          originalGeometryClone.disposeBoundsTree();
        }

        replaceShadedMesh(restoredMesh);
      },
    };
  },

  /** Cancels the slice preview and restores the original view. */
  clearSlicePreview: (shouldClearHistory = true) => {
    set({
      slicingPlane: null,
      geometryHistory: shouldClearHistory ? [] : get().geometryHistory,
    });
  },
}));

export default useSliceStore;
